/* Document detail dialog: overview, nodes, chunks, stats tabs
   (split from CDMDocumentsSettings.jsx) */
import React from 'react';
import { toast } from 'sonner';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import {
  AlertTriangle, Box, Brain, CheckCircle, FileText, Layers, Loader2,
  RefreshCw, Server, Sparkles, Trash2, XCircle,
} from 'lucide-react';
import { API_URL, authedFetch } from './api';
import {
  getLangDisplay, STATUS_ICONS, DOC_TYPE_COLORS, NODE_TYPE_COLORS,
  CHUNK_TYPE_COLORS, formatDate, truncateText,
} from './constants';

export function DocumentDetailDialog(props) {
  const {
  activeTab, aiDetectionResult, chunkSaveSuccess, detailLoading, docChunks,
  docDetail, docNodes, docStats, docSummaryStatus, editingChunk,
  editingChunkText, editingDocSummary, editingDocSummaryText, editingSectionIdx, editingSectionText,
  editingSectionTitle, fetchDocSummaryStatus, fetchDocuments, fetchSystemStats, handleDeleteDocument,
  savingChunk, savingDocSummary, savingSectionIdx, sectionSaveSuccess, selectedDoc,
  setActiveTab, setChunkSaveSuccess, setDocChunks, setDocDetail, setEditingChunk,
  setEditingChunkText, setEditingDocSummary, setEditingDocSummaryText, setEditingSectionIdx, setEditingSectionText,
  setEditingSectionTitle, setSavingChunk, setSavingDocSummary, setSavingSectionIdx, setSectionSaveSuccess,
  setSelectedDoc, startStatusPolling, tensornexStatus, totalChunks, totalNodes,
  } = props;

  return (<>
      {/* Document Detail Dialog */}
      <Dialog open={!!selectedDoc} onOpenChange={() => setSelectedDoc(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="document-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              {docDetail?.title || truncateText(docDetail?.source_uri, 60) || 'Document Details'}
              {/* Phase 11.5: Replace this version — visible on URL/file-upload parents only */}
              {docDetail && !docDetail.parent_doc_id && docDetail.source_type !== 'zoho_kb_article' && (
                <>
                  <input
                    type="file"
                    ref={(el) => { if (el) el._replaceRef = true; }}
                    className="hidden"
                    data-testid="replace-file-input"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      e.target.value = '';
                      toast.info('Uploading replacement file...');
                      try {
                        // Upload file to MinIO via the existing upload endpoint, then replace
                        const formData = new FormData();
                        formData.append('file', file);
                        formData.append('title', file.name.replace(/\.[^/.]+$/, ''));
                        formData.append('collection_id', docDetail.collection_id || 'General');
                        const uploadRes = await authedFetch(`${API_URL}/api/rag/ingest/file`, { method: 'POST', body: formData });
                        if (uploadRes.ok) {
                          // Now trigger version replacement using the existing doc's pipeline worker path
                          const replaceRes = await authedFetch(`${API_URL}/api/rag/cdm/documents/${docDetail.doc_id}/replace`, {
                            method: 'POST', headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ url: docDetail.source_uri }),
                          });
                          const rd = await replaceRes.json();
                          if (rd.success) { toast.success(`Updated to ${rd.new_version}`); setSelectedDoc(null); fetchDocuments(); }
                          else toast.error(rd.detail || 'Replace failed');
                        } else toast.error('File upload failed');
                      } catch (err) { toast.error('Failed: ' + err.message); }
                    }}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-xs h-7 ml-auto shrink-0"
                    data-testid="replace-version-btn"
                    disabled={docDetail.processing_status === 'version_replacing'}
                    title={docDetail.processing_status === 'version_replacing' ? 'Currently updating — try again shortly' : ''}
                    onClick={() => {
                      const isUrl = docDetail.source_type === 'url' || docDetail.doc_type === 'url';
                      if (isUrl) {
                        const newUrl = prompt('Enter the new URL:', docDetail.source_uri || '');
                        if (newUrl && newUrl.trim()) {
                          authedFetch(`${API_URL}/api/rag/cdm/documents/${docDetail.doc_id}/replace`, {
                            method: 'POST', headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ url: newUrl.trim() }),
                          }).then(r => r.json()).then(d => {
                            if (d.success) { toast.success(`Updated to ${d.new_version}. ${d.preserved_chunks} edited chunks preserved.`); setSelectedDoc(null); fetchDocuments(); }
                            else toast.error(d.detail || 'Failed');
                          }).catch(() => toast.error('Failed'));
                        }
                      } else {
                        // File upload: trigger file picker
                        const fileInput = document.querySelector('[data-testid="replace-file-input"]');
                        if (fileInput) fileInput.click();
                      }
                    }}
                  >
                    <RefreshCw className="h-3 w-3 mr-1" /> Replace Version
                  </Button>
                </>
              )}
            </DialogTitle>
          </DialogHeader>

          {detailLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
            </div>
          ) : docDetail ? (
            <>
            {docDetail.processing_status === 'version_replacing' && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-2 mb-2 flex items-center gap-2 text-amber-700 text-sm">
                <Loader2 className="h-4 w-4 animate-spin" />
                This document is being updated to a new version. Some content may be stale.
              </div>
            )}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
              <TabsList className="grid w-full grid-cols-5">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="nodes">Nodes ({totalNodes})</TabsTrigger>
                <TabsTrigger value="chunks">Chunks ({totalChunks})</TabsTrigger>
                <TabsTrigger value="stats">Stats</TabsTrigger>
                <TabsTrigger value="ai-analysis">AI Analysis</TabsTrigger>
              </TabsList>

              {/* Overview Tab */}
              <TabsContent value="overview" className="space-y-4">
                {/* Processing Status Timeline */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm">Processing Status</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between">
                      {['queued', 'processing', 'structured', 'chunked', 'embedded', 'indexed'].map((status, idx) => {
                        const StatusIcon = STATUS_ICONS[status];
                        const isActive = docDetail.processing_status === status;
                        const isPast = ['queued', 'processing', 'structured', 'chunked', 'embedded', 'indexed']
                          .indexOf(docDetail.processing_status) > idx;
                        const isFailed = docDetail.processing_status === 'failed';
                        
                        return (
                          <React.Fragment key={status}>
                            <div className={`flex flex-col items-center ${isActive ? 'text-blue-600' : isPast ? 'text-green-600' : 'text-gray-400'}`}>
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                isActive ? 'bg-blue-100' : isPast ? 'bg-green-100' : 'bg-gray-100'
                              }`}>
                                <StatusIcon className={`h-5 w-5 ${isActive && status === 'processing' ? 'animate-spin' : ''}`} />
                              </div>
                              <span className="text-xs mt-1 capitalize">{status}</span>
                            </div>
                            {idx < 5 && (
                              <div className={`flex-1 h-1 mx-2 ${isPast ? 'bg-green-300' : 'bg-gray-200'}`} />
                            )}
                          </React.Fragment>
                        );
                      })}
                    </div>
                    {docDetail.processing_status === 'failed' && docDetail.error_message && (
                      <div className="mt-4 p-3 bg-red-50 rounded-lg border border-red-200">
                        <div className="flex items-center gap-2 text-red-700">
                          <XCircle className="h-4 w-4" />
                          <span className="font-medium">Error:</span>
                        </div>
                        <p className="text-sm text-red-600 mt-1">{docDetail.error_message}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Document Metadata */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm">Document Metadata</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-gray-500">Document ID</p>
                        <p className="font-mono text-xs break-all">{docDetail.doc_id}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Version ID</p>
                        <p className="font-mono text-xs break-all">{docDetail.version_id || 'N/A'}</p>
                      </div>
                      <div data-testid="detail-source-uri">
                        <p className="text-gray-500">Source URI</p>
                        {docDetail.source_uri && (docDetail.source_uri.startsWith('http://') || docDetail.source_uri.startsWith('https://')) ? (
                          <a href={docDetail.source_uri} target="_blank" rel="noopener noreferrer" className="font-mono text-xs break-all text-blue-600 hover:underline">
                            {docDetail.source_uri}
                          </a>
                        ) : (
                          <p className="font-mono text-xs break-all text-gray-700">{docDetail.source_uri || 'N/A'}</p>
                        )}
                      </div>
                      <div>
                        <p className="text-gray-500">Document Type</p>
                        <Badge className={DOC_TYPE_COLORS[docDetail.doc_type || (docDetail.source_type === 'zoho_kb_article' ? 'article' : 'text')] || 'bg-gray-100'}>
                          {docDetail.doc_type || (docDetail.source_type === 'zoho_kb_article' ? 'article' : 'unknown')}
                        </Badge>
                      </div>
                      <div data-testid="detail-collection">
                        <p className="text-gray-500">Collection</p>
                        <Badge className={
                          (docDetail.collection_id && docDetail.collection_id !== "General")
                            ? "bg-blue-100 text-blue-800"
                            : "bg-gray-100 text-gray-600"
                        }>
                          {docDetail.collection_id || "General"}
                        </Badge>
                      </div>
                      <div>
                        <p className="text-gray-500">Authority Level</p>
                        <Badge variant="outline">{docDetail.domain_authority_level}</Badge>
                      </div>
                      <div data-testid="detail-language">
                        <p className="text-gray-500">Language</p>
                        {(() => {
                          const ld = getLangDisplay(docDetail.language);
                          const isChild = !!docDetail.parent_doc_id;
                          const scanLang = docDetail.scan_options?.language;
                          const isInherited = isChild && scanLang && scanLang !== 'auto' && docDetail.language === scanLang;
                          return (
                            <span className="text-sm">
                              <span className="mr-1">{ld.flag}</span>
                              {ld.code ? `${ld.label} (${ld.code})` : 'Unknown'}
                              {isInherited && <span className="text-[10px] text-gray-400 ml-1">(inherited from parent)</span>}
                            </span>
                          );
                        })()}
                      </div>
                      <div>
                        <p className="text-gray-500">Permissions</p>
                        <p>{docDetail.permissions?.length > 0 ? docDetail.permissions.join(', ') : 'None'}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Ingested At</p>
                        <p>{formatDate(docDetail.ingested_at)}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Updated At</p>
                        <p>{formatDate(docDetail.updated_at)}</p>
                      </div>
                    </div>
                    {docDetail.metadata && Object.keys(docDetail.metadata).length > 0 && (
                      <div className="mt-4 pt-4 border-t">
                        <details>
                          <summary className="text-gray-500 cursor-pointer hover:text-gray-700 text-sm">View Custom Metadata</summary>
                          <pre className="text-xs bg-gray-50 p-2 rounded overflow-auto max-h-40 mt-2">
                            {JSON.stringify(docDetail.metadata, null, 2)}
                          </pre>
                        </details>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Phase 11: Review Controls */}
                <Card data-testid="review-controls-card">
                  <CardContent className="py-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-700">Review Status</p>
                        <p className="text-xs text-gray-400">
                          {docDetail?.review_status === 'reviewed' ? `Reviewed ${docDetail.reviewed_at ? new Date(docDetail.reviewed_at).toLocaleString() : ''}` :
                           docDetail?.review_status === 'edited' ? `Edited ${docDetail.reviewed_at ? new Date(docDetail.reviewed_at).toLocaleString() : ''}` :
                           'Not yet reviewed'}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        {(!docDetail?.review_status || docDetail?.review_status === 'auto_extracted' || docDetail?.review_status === 'edited') && (
                          <Button
                            size="sm"
                            className="text-xs h-7 bg-green-600 hover:bg-green-700"
                            data-testid="mark-reviewed-btn"
                            onClick={async () => {
                              try {
                                const res = await authedFetch(`${API_URL}/api/rag/cdm/documents/${docDetail.doc_id}/review`, {
                                  method: 'PATCH',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ review_status: 'reviewed' }),
                                });
                                if (res.ok) {
                                  toast.success('Marked as reviewed');
                                  // Refresh detail
                                  const detailRes = await authedFetch(`${API_URL}/api/rag/cdm/documents/${docDetail.doc_id}`);
                                  if (detailRes.ok) setDocDetail(await detailRes.json());
                                  fetchDocuments();
                                }
                              } catch (e) { toast.error('Failed'); }
                            }}
                          >
                            <CheckCircle className="h-3 w-3 mr-1" /> Mark as Reviewed
                          </Button>
                        )}
                        {(docDetail?.review_status === 'reviewed' || docDetail?.review_status === 'edited') && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-xs h-7 text-gray-500"
                            onClick={async () => {
                              try {
                                const res = await authedFetch(`${API_URL}/api/rag/cdm/documents/${docDetail.doc_id}/review`, {
                                  method: 'PATCH',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ review_status: 'auto_extracted' }),
                                });
                                if (res.ok) {
                                  toast.success('Reverted to auto-extracted');
                                  const detailRes = await authedFetch(`${API_URL}/api/rag/cdm/documents/${docDetail.doc_id}`);
                                  if (detailRes.ok) setDocDetail(await detailRes.json());
                                  fetchDocuments();
                                }
                              } catch (e) { toast.error('Failed'); }
                            }}
                          >
                            Unmark
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Media Content Breakdown */}
                {docDetail?.media_breakdown && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm">Media Content</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                          <span className="text-lg">🖼️</span>
                          <div>
                            <p className="font-bold text-sm">{docDetail.media_breakdown.images || 0}</p>
                            <p className="text-[10px] text-gray-500">Images</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                          <span className="text-lg">🎥</span>
                          <div>
                            <p className="font-bold text-sm">{docDetail.media_breakdown.videos || 0}</p>
                            <p className="text-[10px] text-gray-500">Videos</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                          <span className="text-lg">📎</span>
                          <div>
                            <p className="font-bold text-sm">{docDetail.media_breakdown.pdfs || 0}</p>
                            <p className="text-[10px] text-gray-500">PDF Links</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                          <span className="text-lg">🔗</span>
                          <div>
                            <p className="font-bold text-sm">{docDetail.media_breakdown.links || 0}</p>
                            <p className="text-[10px] text-gray-500">External Links</p>
                          </div>
                        </div>
                      </div>
                      {docDetail.media_breakdown.youtube_urls?.length > 0 && (
                        <div className="mt-3 space-y-1">
                          <p className="text-xs font-medium text-gray-500">Video URLs:</p>
                          {docDetail.media_breakdown.youtube_urls.map((u, i) => (
                            <a key={i} href={u} target="_blank" rel="noreferrer" className="text-[10px] text-blue-600 hover:underline block truncate">{u}</a>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              {/* Nodes Tab */}
              <TabsContent value="nodes" className="space-y-4">
                {docNodes.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Layers className="h-10 w-10 mx-auto mb-2 opacity-50" />
                    <p>No nodes found</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {docNodes.map((node) => (
                      <Card key={node.node_id} className="hover:shadow-sm transition-shadow">
                        <CardContent className="py-3">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <Badge className={NODE_TYPE_COLORS[node.node_type] || 'bg-gray-100'}>
                                  {node.node_type}
                                </Badge>
                                {node.page_number && (
                                  <span className="text-xs text-gray-500">Page {node.page_number}</span>
                                )}
                                {node.ocr_confidence !== null && (
                                  <span className="text-xs text-gray-500">
                                    OCR: {(node.ocr_confidence * 100).toFixed(0)}%
                                  </span>
                                )}
                              </div>
                              {node.section_heading_path && (
                                <p className="text-xs text-blue-600 mb-1">{node.section_heading_path}</p>
                              )}
                              <p className="text-sm text-gray-700">{node.text_preview}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* Chunks Tab */}
              <TabsContent value="chunks" className="space-y-4">
                {docChunks.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Box className="h-10 w-10 mx-auto mb-2 opacity-50" />
                    <p>No chunks found</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {docChunks.map((chunk) => (
                      <Card key={chunk.chunk_id} className="hover:shadow-sm transition-shadow">
                        <CardContent className="py-3">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <Badge className={CHUNK_TYPE_COLORS[chunk.chunk_type] || 'bg-gray-100'}>
                                  {chunk.chunk_type}
                                </Badge>
                                <span className="text-xs text-gray-500">
                                  {chunk.token_count} tokens
                                </span>
                                {chunk.last_edited_at && (
                                  <Badge className="bg-blue-50 text-blue-600 text-[9px]">Edited</Badge>
                                )}
                              </div>
                              {/* Phase 11.5: Version preservation warning */}
                              {chunk.last_edited_at && docDetail?.last_version_replaced_at &&
                               new Date(chunk.last_edited_at) < new Date(docDetail.last_version_replaced_at) &&
                               (!chunk.version_warning_acknowledged_at || new Date(chunk.version_warning_acknowledged_at) < new Date(docDetail.last_version_replaced_at)) && (
                                <div className="flex items-center gap-2 px-2 py-1 bg-yellow-50 border border-yellow-200 rounded text-[10px] text-yellow-700 mb-1">
                                  <AlertTriangle className="h-3 w-3 shrink-0" />
                                  <span>Preserved across version update on {new Date(docDetail.last_version_replaced_at).toLocaleDateString()} — verify section context still matches.</span>
                                  <button
                                    className="text-yellow-600 hover:text-yellow-800 underline shrink-0"
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      try {
                                        await authedFetch(`${API_URL}/api/rag/cdm/documents/${docDetail.doc_id}/chunks/${chunk.chunk_id}/acknowledge-warning`, { method: 'PATCH' });
                                        const chunkRes = await authedFetch(`${API_URL}/api/rag/cdm/documents/${selectedDoc.doc_id}/chunks?page=1&page_size=50`);
                                        if (chunkRes.ok) { const cd = await chunkRes.json(); setDocChunks(cd.chunks || []); }
                                      } catch (err) { toast.error('Failed'); }
                                    }}
                                  >Mark as verified</button>
                                </div>
                              )}
                              {chunk.section_heading_path && (
                                <p className="text-xs text-blue-600 mb-1">{chunk.section_heading_path}</p>
                              )}
                              {editingChunk === chunk.chunk_id ? (
                                <div className="space-y-2">
                                  <textarea
                                    value={editingChunkText}
                                    onChange={(e) => setEditingChunkText(e.target.value)}
                                    className="w-full text-sm border rounded p-2 min-h-[100px] resize-y focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    data-testid={`chunk-edit-textarea-${chunk.chunk_id}`}
                                  />
                                  <div className="flex gap-2">
                                    <Button
                                      size="sm"
                                      className="text-xs h-7"
                                      disabled={savingChunk}
                                      data-testid={`chunk-save-btn-${chunk.chunk_id}`}
                                      onClick={async () => {
                                        setSavingChunk(true);
                                        try {
                                          const res = await authedFetch(`${API_URL}/api/rag/cdm/documents/${selectedDoc.doc_id}/chunks/${chunk.chunk_id}`, {
                                            method: 'PATCH',
                                            headers: { 'Content-Type': 'application/json' },
                                            body: JSON.stringify({ text: editingChunkText }),
                                          });
                                          if (res.ok) {
                                            setChunkSaveSuccess(chunk.chunk_id);
                                            setTimeout(() => setChunkSaveSuccess(null), 1500);
                                            setEditingChunk(null);
                                            // Refresh chunks
                                            const chunkRes = await authedFetch(`${API_URL}/api/rag/cdm/documents/${selectedDoc.doc_id}/chunks?page=1&page_size=50`);
                                            if (chunkRes.ok) { const cd = await chunkRes.json(); setDocChunks(cd.chunks || []); }
                                            fetchDocuments();
                                          } else {
                                            const err = await res.json();
                                            toast.error(err.detail || 'Failed to save chunk');
                                          }
                                        } catch (e) { toast.error('Failed to save'); }
                                        setSavingChunk(false);
                                      }}
                                    >
                                      {savingChunk ? <><Loader2 className="h-3 w-3 animate-spin mr-1" />Saving...</> : chunkSaveSuccess === chunk.chunk_id ? <><CheckCircle className="h-3 w-3 mr-1 text-green-500" />Saved</> : 'Save'}
                                    </Button>
                                    <Button variant="ghost" size="sm" className="text-xs h-7" onClick={() => setEditingChunk(null)}>Cancel</Button>
                                  </div>
                                  {chunk.original_text && (
                                    <details className="text-[10px] text-gray-400">
                                      <summary className="cursor-pointer hover:text-gray-600">Show original (AI-extracted)</summary>
                                      <p className="mt-1 p-2 bg-gray-50 rounded whitespace-pre-wrap">{chunk.original_text}</p>
                                    </details>
                                  )}
                                </div>
                              ) : (
                                <div>
                                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{chunk.text_preview}</p>
                                  {chunk.original_text && (
                                    <details className="text-[10px] text-gray-400 mt-2">
                                      <summary className="cursor-pointer hover:text-gray-600">Show original (AI-extracted)</summary>
                                      <p className="mt-1 p-2 bg-gray-50 rounded whitespace-pre-wrap">{chunk.original_text}</p>
                                    </details>
                                  )}
                                </div>
                              )}
                            </div>
                            {editingChunk !== chunk.chunk_id && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-xs h-7 shrink-0"
                                onClick={() => { setEditingChunk(chunk.chunk_id); setEditingChunkText(chunk.text_preview || ''); }}
                                data-testid={`chunk-edit-btn-${chunk.chunk_id}`}
                              >
                                Edit
                              </Button>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* Stats Tab */}
              <TabsContent value="stats" className="space-y-4">
                {docStats && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card>
                      <CardContent className="pt-4 text-center">
                        <p className="text-2xl font-bold">{docStats.page_count}</p>
                        <p className="text-sm text-gray-500">Pages</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-4 text-center">
                        <p className="text-2xl font-bold">{docStats.node_count}</p>
                        <p className="text-sm text-gray-500">Nodes</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-4 text-center">
                        <p className="text-2xl font-bold">{docStats.chunk_count}</p>
                        <p className="text-sm text-gray-500">Chunks</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-4 text-center">
                        <p className="text-2xl font-bold">
                          {(() => {
                            const conf = docDetail?.confidence || docStats?.confidence;
                            if (conf && conf.mean_grade && conf.mean_grade !== 'unspecified') {
                              const score = conf.ocr_score || conf.parse_score || conf.layout_score || conf.mean_score;
                              return `${conf.mean_grade.toUpperCase()}${score ? ` (${(score * 100).toFixed(0)}%)` : ''}`;
                            }
                            return 'N/A';
                          })()}
                        </p>
                        <p className="text-sm text-gray-500">Quality Score</p>
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* Tensornex Index Status */}
                {tensornexStatus && (
                  <Card data-testid="tensornex-index-status">
                    <CardHeader>
                      <CardTitle className="text-sm flex items-center gap-2">
                        <Server className="h-4 w-4" />
                        Tensornex Index Status
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                        {/* DocuLing Processing */}
                        {(selectedDoc?.source_type === 'zoho_kb_article' || selectedDoc?.doc_type === 'url' || selectedDoc?.doc_type === 'video' || selectedDoc?.doc_type === 'audio') && (
                          <div className="flex items-center gap-2 p-2 rounded-lg bg-gray-50">
                            <div className="w-2.5 h-2.5 rounded-full bg-green-500" />
                            <div>
                              <p className="font-medium text-xs">DocuLing</p>
                              <p className="text-[10px] text-gray-500">Converted to Markdown</p>
                            </div>
                          </div>
                        )}

                        {/* BGE-M3 Vectors */}
                        <div className="flex items-center gap-2 p-2 rounded-lg bg-gray-50">
                          <div className={`w-2.5 h-2.5 rounded-full ${tensornexStatus?.qdrant_bge_m3?.vectors_found ? 'bg-green-500' : tensornexStatus?.qdrant_bge_m3?.error ? 'bg-red-400' : 'bg-gray-300'}`} />
                          <div>
                            <p className="font-medium text-xs">BGE-M3 (1024d)</p>
                            <p className="text-[10px] text-gray-500">
                              {tensornexStatus?.qdrant_bge_m3?.vectors_found ? 'Indexed' : tensornexStatus?.qdrant_bge_m3?.error ? `Error: ${tensornexStatus.qdrant_bge_m3.error.substring(0,40)}` : 'Checking...'}
                              {tensornexStatus?.qdrant_bge_m3?.expected_chunks ? ` — ${tensornexStatus.qdrant_bge_m3.expected_chunks} chunks` : ''}
                            </p>
                          </div>
                        </div>

                        {/* MiniLM Vectors */}
                        <div className="flex items-center gap-2 p-2 rounded-lg bg-gray-50">
                          <div className={`w-2.5 h-2.5 rounded-full ${tensornexStatus?.qdrant_minilm?.vectors_found ? 'bg-green-500' : 'bg-gray-300'}`} />
                          <div>
                            <p className="font-medium text-xs">MiniLM (384d)</p>
                            <p className="text-[10px] text-gray-500">
                              {tensornexStatus?.qdrant_minilm?.vectors_found ? 'Indexed' : 'Checking...'}
                            </p>
                          </div>
                        </div>

                        {/* MinIO — only show for file uploads */}
                        {selectedDoc?.source_type !== 'zoho_kb_article' && selectedDoc?.doc_type !== 'url' && (
                          <div className="flex items-center gap-2 p-2 rounded-lg bg-gray-50">
                            <div className={`w-2.5 h-2.5 rounded-full ${tensornexStatus?.minio?.file_exists ? 'bg-green-500' : 'bg-gray-300'}`} />
                            <div>
                              <p className="font-medium text-xs">MinIO File</p>
                              <p className="text-[10px] text-gray-500">
                                {tensornexStatus?.minio?.file_exists ? 'Stored' : tensornexStatus?.minio?.file_not_uploaded ? 'MongoDB only' : 'Not found'}
                              </p>
                            </div>
                          </div>
                        )}

                        {/* MongoDB */}
                        <div className="flex items-center gap-2 p-2 rounded-lg bg-gray-50">
                          <div className="w-2.5 h-2.5 rounded-full bg-green-500" />
                          <div>
                            <p className="font-medium text-xs">MongoDB</p>
                            <p className="text-[10px] text-gray-500">
                              {tensornexStatus.mongodb ? `${tensornexStatus.mongodb.nodes}n / ${tensornexStatus.mongodb.chunks}c / ${tensornexStatus.mongodb.pages}p` : 'N/A'}
                            </p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Summary Status */}
                <Card data-testid="doc-summary-status-card">
                  <CardHeader>
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Sparkles className="h-4 w-4 text-amber-500" />
                      Summaries (3-Hop Search)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {docSummaryStatus?.has_doc_summary ? (
                      <div className="space-y-3">
                        <div className="flex items-start gap-2">
                          <div className="w-2.5 h-2.5 rounded-full bg-green-500 mt-1 shrink-0" />
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <p className="font-medium text-xs">Document Summary</p>
                              {!editingDocSummary && (
                                <Button variant="ghost" size="sm" className="text-[10px] h-5" onClick={() => { setEditingDocSummary(true); setEditingDocSummaryText(docSummaryStatus.doc_summary?.summary || ''); }}>Edit</Button>
                              )}
                            </div>
                            {editingDocSummary ? (
                              <div className="space-y-2 mt-1">
                                <textarea
                                  value={editingDocSummaryText}
                                  onChange={(e) => setEditingDocSummaryText(e.target.value)}
                                  className="w-full text-[10px] border rounded p-2 min-h-[60px] resize-y focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                                <div className="flex gap-2">
                                  <Button size="sm" className="text-[10px] h-5" disabled={savingDocSummary} onClick={async () => {
                                    setSavingDocSummary(true);
                                    try {
                                      const res = await authedFetch(`${API_URL}/api/rag/cdm/documents/${selectedDoc.doc_id}/summary`, {
                                        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
                                        body: JSON.stringify({ document_summary: editingDocSummaryText }),
                                      });
                                      if (res.ok) { toast.success('Summary saved'); setEditingDocSummary(false); fetchDocSummaryStatus(selectedDoc.doc_id); fetchDocuments(); }
                                      else toast.error('Failed to save');
                                    } catch (e) { toast.error('Failed'); }
                                    setSavingDocSummary(false);
                                  }}>
                                    {savingDocSummary ? <><Loader2 className="h-3 w-3 animate-spin mr-1" />Saving...</> : 'Save'}
                                  </Button>
                                  <Button variant="ghost" size="sm" className="text-[10px] h-5" onClick={() => setEditingDocSummary(false)}>Cancel</Button>
                                </div>
                              </div>
                            ) : (
                              <p className="text-[10px] text-gray-600 mt-0.5 bg-gray-50 p-2 rounded whitespace-pre-wrap">{docSummaryStatus.doc_summary?.summary}</p>
                            )}
                            {docDetail?.metadata?.original_document_summary && !editingDocSummary && (
                              <details className="text-[9px] text-gray-400 mt-1">
                                <summary className="cursor-pointer hover:text-gray-600">Show original (AI-generated)</summary>
                                <p className="mt-1 p-2 bg-gray-50 rounded whitespace-pre-wrap">{docDetail.metadata.original_document_summary}</p>
                              </details>
                            )}
                            <p className="text-[9px] text-gray-400 mt-0.5">Model: {docSummaryStatus.doc_summary?.model} | {docSummaryStatus.doc_summary?.generated_at ? new Date(docSummaryStatus.doc_summary.generated_at).toLocaleString() : ''}</p>
                          </div>
                        </div>
                        {docSummaryStatus.section_count > 0 && (
                          <div>
                            <p className="font-medium text-xs mb-1 flex items-center gap-1">
                              <span className="w-2.5 h-2.5 rounded-full bg-green-500 inline-block" />
                              Section Summaries ({docSummaryStatus.section_count})
                            </p>
                            <div className="space-y-1 ml-4">
                              {docSummaryStatus.section_summaries?.map((s, i) => (
                                <div key={i} className="text-[10px] text-gray-600 bg-gray-50 p-1.5 rounded">
                                  <div className="flex items-center justify-between">
                                    <span className="font-medium">{s.section_path}:</span>
                                    {editingSectionIdx !== i && (
                                      <Button variant="ghost" size="sm" className="text-[9px] h-4 px-1" onClick={() => { setEditingSectionIdx(i); setEditingSectionTitle(s.section_path || ''); setEditingSectionText(s.summary || ''); }}>Edit</Button>
                                    )}
                                  </div>
                                  {editingSectionIdx === i ? (
                                    <div className="space-y-1 mt-1">
                                      <Input
                                        value={editingSectionTitle}
                                        onChange={(e) => setEditingSectionTitle(e.target.value)}
                                        className="text-[10px] h-6"
                                        placeholder="Section title"
                                      />
                                      <textarea
                                        value={editingSectionText}
                                        onChange={(e) => setEditingSectionText(e.target.value)}
                                        className="w-full text-[10px] border rounded p-1.5 min-h-[50px] resize-y focus:outline-none focus:ring-2 focus:ring-blue-500"
                                      />
                                      <div className="flex gap-1">
                                        <Button size="sm" className="text-[9px] h-4 px-2" disabled={savingSectionIdx === i} onClick={async () => {
                                          setSavingSectionIdx(i);
                                          try {
                                            const payload = { section_path: s.section_path, summary: editingSectionText };
                                            if (editingSectionTitle !== s.section_path) payload.new_section_path = editingSectionTitle;
                                            const res = await authedFetch(`${API_URL}/api/rag/cdm/documents/${selectedDoc.doc_id}/summary`, {
                                              method: 'PATCH', headers: { 'Content-Type': 'application/json' },
                                              body: JSON.stringify({ section_summaries: [payload] }),
                                            });
                                            if (res.ok) {
                                              setSectionSaveSuccess(i);
                                              setTimeout(() => setSectionSaveSuccess(null), 1500);
                                              setEditingSectionIdx(null);
                                              fetchDocSummaryStatus(selectedDoc.doc_id);
                                              fetchDocuments();
                                            } else {
                                              const err = await res.json();
                                              toast.error(err.detail || 'Failed');
                                            }
                                          } catch (e) { toast.error('Failed'); }
                                          setSavingSectionIdx(null);
                                        }}>
                                          {savingSectionIdx === i ? <><Loader2 className="h-3 w-3 animate-spin mr-0.5" />Saving...</> : sectionSaveSuccess === i ? <><CheckCircle className="h-3 w-3 mr-0.5 text-green-500" />Saved</> : 'Save'}
                                        </Button>
                                        <Button variant="ghost" size="sm" className="text-[9px] h-4 px-1" onClick={() => setEditingSectionIdx(null)}>Cancel</Button>
                                      </div>
                                    </div>
                                  ) : (
                                    <p className="mt-0.5 max-h-24 overflow-y-auto whitespace-pre-wrap">{s.summary}</p>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-xs text-gray-400">
                        <div className="w-2.5 h-2.5 rounded-full bg-gray-300" />
                        <span>No summaries generated. Enable summary generation in settings and upload a new document or click "Generate Summaries for All Documents".</span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
              <TabsContent value="ai-analysis" className="space-y-4">
                {!aiDetectionResult || !aiDetectionResult.detection_performed ? (
                  <div className="text-center py-8 text-gray-500">
                    <Brain className="h-10 w-10 mx-auto mb-2 opacity-50" />
                    <p className="font-medium">AI Detection Not Performed</p>
                    <p className="text-xs mt-1">AI detection was not enabled when this document was processed, or no results are available.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Final Decision */}
                    <Card>
                      <CardContent className="pt-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-gray-500">Final Decision</p>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge className={
                                aiDetectionResult.final_verdict === 'OCR' ? 'bg-orange-100 text-orange-800 text-lg px-3 py-1' :
                                aiDetectionResult.final_verdict === 'STANDARD' ? 'bg-blue-100 text-blue-800 text-lg px-3 py-1' :
                                'bg-gray-100 text-gray-800 text-lg px-3 py-1'
                              } data-testid="ai-final-verdict">
                                {aiDetectionResult.final_verdict}
                              </Badge>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-gray-500">Parser Used</p>
                            <p className="font-medium text-sm mt-1" data-testid="ai-parser-used">
                              {aiDetectionResult.parser_to_use === 'tensornex_ocr' ? 'Tensornex OCR' :
                               aiDetectionResult.parser_to_use === 'tensornex_standard' ? 'Tensornex Standard' : 'Fallback'}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Model Votes Table */}
                    {aiDetectionResult.votes && aiDetectionResult.votes.length > 0 && (
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-sm">Model Votes</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm" data-testid="ai-votes-table">
                              <thead>
                                <tr className="border-b text-left text-gray-500">
                                  <th className="pb-2 pr-4">Model</th>
                                  <th className="pb-2 pr-4">Verdict</th>
                                  <th className="pb-2 pr-4">Confidence</th>
                                  <th className="pb-2">Reason</th>
                                </tr>
                              </thead>
                              <tbody>
                                {aiDetectionResult.votes.map((vote, idx) => (
                                  <tr key={idx} className="border-b last:border-0">
                                    <td className="py-2 pr-4 font-mono text-xs">{vote.model}</td>
                                    <td className="py-2 pr-4">
                                      <Badge className={
                                        vote.verdict === 'OCR' ? 'bg-orange-100 text-orange-800' :
                                        vote.verdict === 'STANDARD' ? 'bg-blue-100 text-blue-800' :
                                        'bg-gray-100 text-gray-600'
                                      }>
                                        {vote.verdict}
                                      </Badge>
                                    </td>
                                    <td className="py-2 pr-4">
                                      <div className="flex items-center gap-2">
                                        <div className="w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
                                          <div
                                            className={`h-full rounded-full ${
                                              vote.confidence >= 70 ? 'bg-green-500' :
                                              vote.confidence >= 40 ? 'bg-yellow-500' : 'bg-red-400'
                                            }`}
                                            style={{ width: `${vote.confidence}%` }}
                                          />
                                        </div>
                                        <span className="text-xs font-medium">{vote.confidence}%</span>
                                      </div>
                                    </td>
                                    <td className="py-2 text-xs text-gray-600 max-w-xs truncate">{vote.reason}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Voting Summary */}
                    {aiDetectionResult.weighted_scores && (
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-sm">Voting Summary</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-2">
                            <div className="flex items-center justify-between text-sm">
                              <span className="flex items-center gap-2">
                                <Badge className="bg-orange-100 text-orange-800">OCR</Badge>
                                <span className="text-gray-500">Weighted Score</span>
                              </span>
                              <span className="font-bold" data-testid="ai-ocr-score">{aiDetectionResult.weighted_scores.OCR}</span>
                            </div>
                            <div className="flex items-center justify-between text-sm">
                              <span className="flex items-center gap-2">
                                <Badge className="bg-blue-100 text-blue-800">STANDARD</Badge>
                                <span className="text-gray-500">Weighted Score</span>
                              </span>
                              <span className="font-bold" data-testid="ai-standard-score">{aiDetectionResult.weighted_scores.STANDARD}</span>
                            </div>
                            {aiDetectionResult.weighted_scores.OCR === aiDetectionResult.weighted_scores.STANDARD &&
                             aiDetectionResult.weighted_scores.OCR > 0 && (
                              <p className="text-xs text-amber-600 mt-1">Tie detected — defaulted to OCR (safer for text extraction)</p>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Prompt Used */}
                    {aiDetectionResult.prompt_used && (
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-sm">Prompt Used</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <pre className="text-xs bg-gray-50 p-3 rounded-lg whitespace-pre-wrap font-mono max-h-32 overflow-y-auto">
                            {aiDetectionResult.prompt_used}
                          </pre>
                        </CardContent>
                      </Card>
                    )}

                    {/* Skip reason */}
                    {aiDetectionResult.skip_reason && (
                      <div className="p-3 bg-amber-50 rounded-lg text-xs text-amber-700">
                        Detection skipped: {aiDetectionResult.skip_reason}
                      </div>
                    )}

                    <p className="text-xs text-gray-400">Detected at: {aiDetectionResult.detected_at}</p>
                  </div>
                )}
              </TabsContent>
            </Tabs>
            </>
          ) : null}

          {/* Actions */}
          {selectedDoc && (
            <div className="flex flex-wrap justify-end gap-2 pt-4 border-t">
              {/* Reprocess buttons based on current status */}
              {selectedDoc.processing_status === 'chunked' && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    data-testid="mark-indexed-btn"
                    onClick={async () => {
                      try {
                        const res = await authedFetch(`${API_URL}/api/rag/cdm/documents/${selectedDoc.doc_id}/mark-indexed`, { method: 'POST' });
                        const data = await res.json();
                        if (data.success) { toast.success('Document marked as indexed'); fetchDocuments(); fetchSystemStats(); setSelectedDoc(null); }
                        else toast.error(data.detail || 'Failed');
                      } catch (e) { toast.error('Failed: ' + e.message); }
                    }}
                  >
                    <CheckCircle className="h-4 w-4 mr-1" />Mark as Indexed
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    data-testid="reprocess-embedding-btn"
                    onClick={async () => {
                      try {
                        const res = await authedFetch(`${API_URL}/api/rag/cdm/documents/${selectedDoc.doc_id}/reprocess`, {
                          method: 'POST', headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ from_stage: 'chunked' }),
                        });
                        const data = await res.json();
                        if (data.success) { toast.success('Document queued for re-embedding'); fetchDocuments(); fetchSystemStats(); setSelectedDoc(null); }
                        else toast.error(data.detail || 'Failed');
                      } catch (e) { toast.error('Failed: ' + e.message); }
                    }}
                  >
                    <RefreshCw className="h-4 w-4 mr-1" />Reprocess Embedding
                  </Button>
                </>
              )}
              {selectedDoc.processing_status === 'structured' && (
                <Button
                  variant="outline"
                  size="sm"
                  data-testid="reprocess-chunking-btn"
                  onClick={async () => {
                    try {
                      const res = await authedFetch(`${API_URL}/api/rag/cdm/documents/${selectedDoc.doc_id}/reprocess`, {
                        method: 'POST', headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ from_stage: 'structured' }),
                      });
                      const data = await res.json();
                      if (data.success) { toast.success('Document queued for re-chunking'); fetchDocuments(); fetchSystemStats(); setSelectedDoc(null); }
                      else toast.error(data.detail || 'Failed');
                    } catch (e) { toast.error('Failed: ' + e.message); }
                  }}
                >
                  <RefreshCw className="h-4 w-4 mr-1" />Reprocess Chunking
                </Button>
              )}
              {(selectedDoc.processing_status === 'failed' || selectedDoc.processing_status === 'processing') && (
                <Button
                  variant="outline"
                  size="sm"
                  data-testid="retry-from-start-btn"
                  onClick={async () => {
                    try {
                      const token = localStorage.getItem('token');
                      const res = await authedFetch(`${API_URL}/api/rag/cdm/documents/${selectedDoc.doc_id}/retry`, {
                        method: 'POST', headers: { Authorization: `Bearer ${token}` },
                      });
                      const data = await res.json();
                      if (data.success) { toast.success('Document queued for reprocessing'); fetchDocuments(); fetchSystemStats(); setSelectedDoc(null); startStatusPolling(); }
                      else toast.error(data.detail || 'Failed');
                    } catch (e) { toast.error('Failed: ' + e.message); }
                  }}
                >
                  <RefreshCw className="h-4 w-4 mr-1" />Retry
                </Button>
              )}
              <Button
                variant="destructive"
                size="sm"
                onClick={() => handleDeleteDocument(selectedDoc.doc_id)}
                data-testid="delete-document-btn"
              >
                <Trash2 className="h-4 w-4 mr-1" />Delete
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
  </>);
}
